import { createContext, useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import { categoriasPrisma } from '../data/categorias-prisma';

const PrismaContext = createContext(null);

export const PrismaProvider = ({ children }) => {
    const router = useRouter();

    // Detectar si estamos en la versión en inglés
    const isEnglishVersion = useMemo(() => {
        const rawPath = typeof window === 'undefined' ? router.pathname : (router.asPath || router.pathname);
        const pathname = (rawPath || '').replace(/\/$/, '');
        return pathname.startsWith('/en/prisma');
    }, [router.pathname, router.asPath]);

    // Idioma actual
    const language = isEnglishVersion ? 'en' : 'es';

    // Función helper para obtener el nombre de una categoría según el idioma
    const getCategoryName = (categoryId) => {
        const categoria = categoriasPrisma[categoryId];
        if (!categoria) return 'Categoría';
        return isEnglishVersion ? categoria.englishName : categoria.name;
    };

    // Función helper para obtener el color de una categoría
    const getCategoryColor = (categoryId) => {
        const categoria = categoriasPrisma[categoryId];
        return categoria?.color || '#1976d2';
    };

    // Función helper para obtener el color de fondo de una categoría
    const getCategoryBackgroundColor = (categoryId) => {
        const categoria = categoriasPrisma[categoryId];
        return categoria?.backgroundColor || '#1976d2';
    };

    // Función helper para obtener el label de navegación
    const getNavigationLabel = (key) => {
        const labels = {
            inicio: {
                es: 'Inicio',
                en: 'Home',
            },
            verMas: {
                es: 'Ver más',
                en: 'See more',
            },
            categorias: {
                es: 'Categorías',
                en: 'Categories',
            },
            enlacesUtiles: {
                es: 'Enlaces útiles',
                en: 'Useful Links',
            },
            contacto: {
                es: 'Contacto',
                en: 'Contact',
            },
            politicaPrivacidad: {
                es: 'Política de Privacidad',
                en: 'Privacy Policy',
            },
            terminosCondiciones: {
                es: 'Términos y Condiciones',
                en: 'Terms and Conditions',
            },
            analiticaFantasy: {
                es: 'Analítica Fantasy',
                en: 'Analítica Fantasy',
            },
            siguenos: {
                es: 'Síguenos en Twitter',
                en: 'Follow us on Twitter',
            },
            copyright: {
                es: 'Todos los derechos reservados',
                en: 'All rights reserved',
            },
        };
        return labels[key]?.[language] || key;
    };

    // Función helper para formatear fechas según el idioma
    const formatDate = (date, options = {}) => {
        const locale = isEnglishVersion ? 'en-GB' : 'es-ES';
        return new Date(date).toLocaleDateString(locale, options);
    };

    // Función helper para formatear fechas cortas (día y mes)
    const formatShortDate = (date) => {
        return formatDate(date, {
            day: 'numeric',
            month: 'short',
        });
    };

    // Función helper para formatear fechas largas (día, mes y año)
    const formatLongDate = (date) => {
        return formatDate(date, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    // Función helper para obtener "Hace X días/horas/minutos" traducido
    const getTimeAgo = (dateString) => {
        if (!dateString) return '';

        const currentDate = new Date();
        const date = new Date(dateString);

        // Validar que la fecha sea válida
        if (isNaN(date.getTime())) {
            return '';
        }

        const diff = currentDate - date;

        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        const months = Math.floor(days / 31);

        // Textos en español e inglés
        const texts = {
            es: {
                second: 'segundo',
                seconds: 'segundos',
                minute: 'minuto',
                minutes: 'minutos',
                hour: 'hora',
                hours: 'horas',
                day: 'día',
                days: 'días',
                month: 'mes',
                months: 'meses',
                ago: 'Hace',
            },
            en: {
                second: 'second',
                seconds: 'seconds',
                minute: 'minute',
                minutes: 'minutes',
                hour: 'hour',
                hours: 'hours',
                day: 'day',
                days: 'days',
                month: 'month',
                months: 'months',
                ago: '',
            },
        };

        const t = texts[language];

        if (seconds < 60) {
            const word = seconds === 1 ? t.second : t.seconds;
            return isEnglishVersion
                ? `${seconds >= 0 ? seconds : 0} ${word} ago`
                : `${t.ago} ${seconds >= 0 ? seconds : 0} ${word}`;
        } else if (minutes < 60) {
            const word = minutes === 1 ? t.minute : t.minutes;
            return isEnglishVersion
                ? `${minutes >= 0 ? minutes : 0} ${word} ago`
                : `${t.ago} ${minutes >= 0 ? minutes : 0} ${word}`;
        } else if (hours < 24) {
            const word = hours === 1 ? t.hour : t.hours;
            return isEnglishVersion
                ? `${hours >= 0 ? hours : 0} ${word} ago`
                : `${t.ago} ${hours >= 0 ? hours : 0} ${word}`;
        } else if (days < 31) {
            const word = days === 1 ? t.day : t.days;
            return isEnglishVersion
                ? `${days >= 0 ? days : 0} ${word} ago`
                : `${t.ago} ${days >= 0 ? days : 0} ${word}`;
        } else {
            const word = months === 1 ? t.month : t.months;
            return isEnglishVersion
                ? `${months >= 0 ? months : 0} ${word} ago`
                : `${t.ago} ${months >= 0 ? months : 0} ${word}`;
        }
    };

    const value = {
        isEnglishVersion,
        language,
        getCategoryName,
        getCategoryColor,
        getCategoryBackgroundColor,
        getNavigationLabel,
        formatDate,
        formatShortDate,
        formatLongDate,
        getTimeAgo,
    };

    return <PrismaContext.Provider value={value}>{children}</PrismaContext.Provider>;
};

// Hook personalizado para usar el contexto
export const usePrismaContext = () => {
    const context = useContext(PrismaContext);
    if (!context) {
        throw new Error('usePrismaContext debe ser usado dentro de un PrismaProvider');
    }
    return context;
};
